import { LOCAL_URL } from "../../constants/apiUrls";
import { apiSlice } from "./../apiSlice";

export const usersApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    verification: builder.mutation({
      query: (data) => ({
        url: `api/user/verify-email/`,
        method: "POST",
        body: data,
      }),
    }),
    mobileVerification: builder.mutation({
      query: (data) => ({
        url: `api/user/verify-mobile/`,
        method: "POST",
        body: data,
      }),
    }),
    login: builder.mutation({
      query: (data) => ({
        url: `api/user/token/`,
        method: "POST",
        body: data,
      }),
    }),
    forgot: builder.mutation({
      query: (data) => ({
        url: `api/user/forgot-password/`,
        method: "POST",
        body: data,
      }),
    }),
    resetPassword: builder.mutation({
      query: (data) => ({
        url: `api/user/password-reset-confirm/`,
        method: "POST",
        body: data,
      }),
    }),
    resetSetTokenVerify: builder.mutation({
      query: (data) => ({
        url: `api/user/password-reset-token-check/`,
        method: "POST",
        body: data,
      }),
    }),
    register: builder.mutation({
      query: (data, userToken) => ({
        url: `api/user/create/`,
        method: "POST",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    getUser: builder.query({
      query: (token) => ({
        url: `api/user/me/`,
        headers: {
          "content-type": "application/json",
        },
      }),
      /* This is the read RootLayout holds for the whole signed-in session,
         and whose answer it writes into auth.userInfo — which is where every
         screen looks up who the viewer is and which companies they hold.
         Untagged, it kept serving the record from before the claim, so the
         profile the user had just been handed went on offering "Request
         access". Claiming (and buying) already invalidate "User"; this is
         what makes that invalidation reach the viewer's own record. */
      providesTags: ["User"],
    }),
    getUserDetails: builder.mutation({
      query: (token) => ({
        url: `api/user/me/`,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    getUserVerificationStatus: builder.query({
      query: (token) => ({
        url: `api/user/sumsub/check_status/`,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    generateSumsubUrl: builder.mutation({
      query: (token) => ({
        url: `api/user/sumsub/generate_verification_url/`,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    updateUser: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/user/me/`,
        method: "PATCH",
        body: data,
        headers: {
          "content-type": "application/json",
        },
      }),
    }),
    updateUserImage: builder.mutation({
      query: ({ data, token }) => ({
        url: `api/user/me/`,
        method: "PATCH",
        body: data,
      }),
    }),
    updateNotification: builder.mutation({
      query: ({ notificationId, token }) => ({
        url: `api/user/notifications/${notificationId}/`,
        method: "PUT",
        body: { is_read: true }, // You're setting is_read to true
        headers: {
          "Content-Type": "application/json",
        },
      }),
    }),
    logOutUser: builder.mutation({
      // The `token` argument is still accepted and ignored: callers pass it
      // and the session travels in the cookie now. Dropping the parameter
      // would mean touching every call site for no behavioural gain.
      query: () => ({
        url: `api/user/logout/`,
        method: "POST",
      }),
    }),

    /* "Am I signed in?", answered by the cookie the browser already holds. */
    getCurrentSession: builder.query({
      query: () => ({
        url: `api/user/session/`,
      }),
    }),

    /** "Where you're signed in" — the user's own device list. */
    getUserSessions: builder.query({
      query: () => ({
        url: `api/user/sessions/`,
      }),
      providesTags: ["UserSessions"],
    }),

    revokeUserSession: builder.mutation({
      query: (sid) => ({
        url: `api/user/sessions/${encodeURIComponent(sid)}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["UserSessions"],
    }),
  }),
});

export const {
  useVerificationMutation,
  useGetCurrentSessionQuery,
  useLazyGetCurrentSessionQuery,
  useGetUserSessionsQuery,
  useRevokeUserSessionMutation,
  useMobileVerificationMutation,
  useLoginMutation,
  useForgotMutation,
  useResetPasswordMutation,
  useResetSetTokenVerifyMutation,
  useRegisterMutation,
  useGetUserQuery,
  useGetUserDetailsMutation,
  useGetUserVerificationStatusQuery,
  useGenerateSumsubUrlMutation,
  useUpdateUserMutation,
  useUpdateUserImageMutation,
  useUpdateNotificationMutation,
  useLogOutUserMutation
} = usersApiSlice;
